import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Moment from 'moment';
import { toast } from 'react-toastify';

import { makeStyles, useTheme } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { DataGridPro, useGridApiRef as useApiRef } from '@mui/x-data-grid-pro';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ImportExportIcon from '@mui/icons-material/ImportExport';

import SearchBar from '../../../components/searchbar';
import ConfirmationDialog from '../../../components/confirmationdlg';

import AssetsService, { fields } from '../../../data/assets-service';
import DataContext from '../../../data/data-context';

import useWindowSize from '../../../libraries/usewindowsize';
import { SaveFile } from '../../../libraries/helpers';
import Auth, { ProfileContext } from '../../../data/auth-service';
import { LoadingOverlay, NoRowsOverlay } from '../../../components/xgrid/overlays';
import FileUpload from '../../../components/file-upload';

const useStyles = makeStyles(theme => ({
    buttonRight: {
        float: 'right',
    },
    roleUsers: {
        display: 'inline',
    },
    fullWidth: {
        width: '100%',
        // marginBottom: 24,
    },
    tabBar: {
        marginTop: theme.spacing(3),
    },
    dialogHighlight: {
        color: theme.palette.secondary.main,
    },
    tableHeader: {
        display: 'flex',
        justifyContent: 'flex-end',
        padding: '8px',
        borderBottom: `1px solid rgba(${theme.palette.mode === 'dark' ? '81, 81, 81' : '224, 224, 224'}, 1)`,
    },
    hideFileInput: {
        display: 'none',
    },
}));

const Assets = () => {
    const classes = useStyles();
    const theme = useTheme();
    const navigate = useNavigate();
    const [assetData, setAssetData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [search, setSearch] = useState();
    const { assets, refreshAssets, deleteAsset } = useContext(DataContext);
    const { height } = useWindowSize();
    const tableAPIRef = useApiRef();
    const { updateProfile, getProfile } = useContext(ProfileContext);
    const assetsPerms = Auth.permissions.get('Assets');

    const [columnWidth, setColumnWidth] = useState({});
    const [importing, setImporting] = useState(false);

    const handleColumnResize = params => {
        const widths = tableAPIRef.current.getAllColumns().reduce((a, c) => {
            a[c.headerName] = c.width;
            return a;
        }, {});
        updateProfile('assets', {
            ...getProfile('assets'),
            widths: widths,
        });
        setColumnWidth(widths);
    };

    const handleColumnReorder = () => {
        let order = [];
        setTimeout(() => {
            order = tableAPIRef.current.getAllColumns().reduce((a, c) => {
                a.push(c.headerName);
                return a;
            }, []);

            updateProfile('assets', {
                ...getProfile('assets'),
                columns: order,
            });
        });
    };

    // fetch from cache, filter on search
    const getAssets = useCallback(() => {
        setLoading(true);
        const _search = (search || '').toLowerCase();
        setAssetData(Object.values(assets)
            .filter(a =>
                !_search.length ||
                (a.name || '').toLowerCase().indexOf(_search) >= 0 ||
                (a.ip || '').toLowerCase().indexOf(_search) >= 0));
        setLoading(false);
    }, [assets, search]);

    const handleCreate = () => {
        navigate('/system/assets/create');
    };

    const handleSearchChange = ({ search }) => {
        setSearch(search);
    };

    const handleSelectAsset = (s) => {
        setSelectedAsset(s.row);
    };

    /* eslint-disable react/display-name */
    const tableRenderers = {
        name: d => { return <div>{d.row.name}</div> },
        ip: d => { return <div>{d.row.ip}</div> },
        created: d => { return <div>{Moment(d.row.created).format(theme.date.long)}</div> },
    };

    const columns = fields.map(f => {
        return {
            ...f,
            sortable: true,
            width: columnWidth[f.title] || 0,
            ...(!columnWidth[f.title] ? { flex: 1 } : {}),
            headerName: f.title,
            valueGetter: (p) => { return p.row[f.variable] },
            type: 'string',
            disableColumnMenu: true,
            hidden: false,
            renderCell: tableRenderers[f.variable],
        };
    });

    const handleEdit = () => {
        navigate(`/system/assets/update/${selectedAsset.ip}`);
    };

    const handleCloseDeleteDialog = () => {
        setDeleteDialog(false);
        setTimeout(() => {
            setSelectedAsset(null);
        }, 1000);
    };

    const handleOpenDelete = () => {
        setDeleteDialog(true);
    };

    const handleImport = data => {
        setImporting(true);
        // TODO: currently will not replace dup IPs, can add GUI for replace dups option
        AssetsService.import({ data, overwrite: false }).then(res => {
            toast.info(`${res.count} new assets were imported`);
            refreshAssets();
        }).finally(() => setImporting(false));
    };

    const handleExport = () => {
        try {
            const data = JSON.stringify(assetData);
            SaveFile(data, 'assets.json', 'text/plain');
        } catch (err) {
            toast.error(`Failed to export assets file: ${err && err.message}`);
            console.error(err);
        }
    };

    const handleDelete = () => {
        // refresh assets context for ip controls
        deleteAsset(selectedAsset.ip).then(res => {
            getAssets(search);
        });
        setSelectedAsset(null);
        setDeleteDialog(false);
    };

    useEffect(() => {
        setColumnWidth(getProfile('assets').widths || {});
    }, [getProfile]);

    useEffect(() => {
        getAssets();
    }, [getAssets]);

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Grid container direction="row" className={classes.fullWidth} mb={3} display="flex" justify="space-between">
                    <Grid xs={6} item>
                        <SearchBar immediate onSearch={handleSearchChange} searchservice={AssetsService} />
                    </Grid>
                    {/* button group */}
                    <Grid container item xs={6}>
                        <Grid container direction="row" justifyContent="flex-end">
                            {/* export */}
                            <Box>
                                <Button variant="contained" color="primary" aria-label="export assets" onClick={handleExport}>
                                    <ImportExportIcon /> Export
                                </Button>
                            </Box>
                            {/* import */}
                            {assetsPerms.admin && <Box ml={1}>
                                <FileUpload
                                    id="assets-import-file"
                                    onImport={handleImport}
                                    component={
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            component="span"
                                            disabled={importing}
                                            aria-label="import assets">
                                            <ImportExportIcon/>{'Import'}
                                        </Button>
                                    }/>
                            </Box>}
                            {/* create */}
                            {assetsPerms.write && <Box ml={1}>
                                <Button variant="contained" color="primary" aria-label="create asset" onClick={handleCreate}>
                                    <AddIcon /> Create Asset
                                </Button>
                            </Box>}
                        </Grid>
                    </Grid>
                </Grid>
                <Box width="100%" height={height - 250}>
                    <DataGridPro
                        apiRef={tableAPIRef}
                        components={{
                            Header: !(assetsPerms.write || assetsPerms.delete) ? undefined : () => {
                                return (
                                    <Box p={2} className={classes.tableHeader}>
                                        {assetsPerms.delete && <Box><IconButton
                                            disabled={!selectedAsset}
                                            variant="contained"
                                            color="secondary"
                                            aria-label="delete asset"
                                            onClick={handleOpenDelete}
                                        >
                                            <DeleteIcon />
                                        </IconButton></Box>}
                                        {assetsPerms.write && <Box ml={1}><IconButton
                                            disabled={!selectedAsset}
                                            variant="contained"
                                            color="primary"
                                            aria-label="edit asset"
                                            onClick={handleEdit}
                                        >
                                            <EditIcon />
                                        </IconButton></Box>}
                                    </Box>
                                );
                            },
                            LoadingOverlay: LoadingOverlay,
                            NoRowsOverlay: NoRowsOverlay,
                        }}
                        loading={loading || importing}
                        rows={assetData}
                        columns={(() => {
                            const p = getProfile('assets');
                            let c = columns;

                            if (p.columns && p.columns.length) {
                                c = p.columns.map(v => {
                                    for (let i = c.length; i--;) {
                                        if (v === c[i].headerName) {
                                            return c[i];
                                        }
                                    }
                                    return v;
                                });
                            }
                            return c;
                        })()}
                        disableMultipleSelection
                        disableColumnFilter
                        disableColumnMenu
                        onRowClick={handleSelectAsset}
                        onColumnResize={handleColumnResize}
                        onColumnOrderChange={handleColumnReorder}
                        getRowId={r => r.ip}
                    />
                </Box>
            </Grid>
            <ConfirmationDialog
                onClose={handleCloseDeleteDialog}
                onOK={handleDelete}
                open={deleteDialog}
                message={(() => {
                    return (
                        <Box>
                            Are you sure you want to remove the asset <span className={classes.dialogHighlight}>{selectedAsset ? selectedAsset.name : ''}</span>?
                        </Box>
                    );
                })()}
                title="Delete Asset"
                cancelLabel="Cancel"
                okLabel="Yes"
            />
        </Grid>
    );
};

export default Assets;
